/** Whether a higher actual spec (vs. Contracted) produces a discount or a premium. */
export enum HigherSpecEffect {
  Discount = 'DISCOUNT',
  Premium = 'PREMIUM',
}

/** Which side of the diff (if any) actually applies a price adjustment. */
export enum AdjustmentMode {
  Discount = 'DISCOUNT',
  Premium = 'PREMIUM',
}

/**
 * Whether a formula row's bound includes the boundary value itself ("<=" / ">=") or not
 * ("<" / ">"). Optional on formula rows — omitting it keeps the long-standing default
 * (lower exclusive, upper inclusive; see findApplicableRowIndex in qualityFormulaMatching.ts).
 */
export enum BoundOperator {
  Exclusive = 'EXCLUSIVE',
  Inclusive = 'INCLUSIVE',
}

/**
 * Which basis a "% of Price" (PERCENTAGE) quality formula scales its percentFactor against,
 * instead of always the request's top-level tradePrice — see ProductPriceBasisSchema in
 * QualityDto.ts for each variant's own fields.
 */
export enum ProductPriceBasisType {
  ContractPrice = 'CONTRACT_PRICE',
  DeemedPrice = 'DEEMED_PRICE',
  PriceComponents = 'PRICE_COMPONENTS',
}

/** How the diff is normalized before scaling by tradePrice (percentage formulas only). */
export enum PercentageBasis {
  FixedHundred = 'FIXED_HUNDRED',
  RelativeToContracted = 'RELATIVE_TO_CONTRACTED',
}

/**
 * Quality attribute keys for the quality-adjustments calculate endpoint.
 * MOISTURE represents moisture content — may drive a quantity OR a price adjustment.
 * NITROGEN represents nitrogen content — quantity-only (see QuantityAdjustedByPercent).
 */
export enum SpecType {
  Ash = 'ASH',
  Btu = 'BTU',
  Hgi = 'HGI',
  Kcal = 'KCAL',
  Moisture = 'MOISTURE',
  Nitrogen = 'NITROGEN',
  Sulfur = 'SULFUR',
  Volatile = 'VOLATILE',
}

/** Whether a quality spec adjusts the invoice quantity or the unit price. */
export enum QualityAdjustmentType {
  Price = 'PRICE',
  Quantity = 'QUANTITY',
}

/** Which calculation family to apply for a quality spec. */
export enum QualityFormulaType {
  AdjustedSalesPrice = 'ADJUSTED_SALES_PRICE',
  Percentage = 'PERCENTAGE',
  PerUnit = 'PER_UNIT',

  /**
   * Billable quantity = ShippedQuantity x (ActualSpec / 100) — unconditional, no contracted
   * baseline and no tiers (e.g. Nitrogen: pricing assumes 100% content, actual analysis scales
   * the invoice quantity down).
   */
  QuantityAdjustedByPercent = 'QUANTITY_ADJUSTED_BY_PERCENT',
  QuantityAdjustedToContracted = 'QUANTITY_ADJUSTED_TO_CONTRACTED',
  QuantityAdjustedToDry = 'QUANTITY_ADJUSTED_TO_DRY',
}

/**
 * How a Percentage/PerUnit spec's tiers are structured — a second discriminant (alongside
 * formulaType) with its own distinct request shape (see PercentageQualitySpecSchema etc.):
 * Threshold — 1-2 sides anchored to a single guaranteed/contracted value, each side's own bound
 * fully derived server-side from its adjustmentMode vs. The spec type's natural higher-value
 * effect, so callers never send bounds for it.
 * Range — an ordered chain of explicit, independently-bounded value intervals.
 */
export enum TierMode {
  Range = 'RANGE',
  Threshold = 'THRESHOLD',
}

/** How one adjacent pair of a Range formula's rows relates to each other — see `findRangeRowIssues`. */
export enum RangeRowIssueType {
  Gap = 'GAP',
  Overlap = 'OVERLAP',
  Unsorted = 'UNSORTED',
}
